/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes, Children } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import '@/pdp/components/GalleryGrid/GalleryGrid.css';

export interface GalleryGridProps extends HTMLAttributes<HTMLDivElement> {
  children: VNode[] | VNode;
  gap?: 'small' | 'medium' | 'large' | null;
  style?: Record<string, string | number>;
}

export const GalleryGrid: FunctionComponent<GalleryGridProps> = ({
  children,
  gap = null,
  className,
  style,
  ...props
}) => {
  const count = Children.count(children);

  return (
    <div
      {...props}
      className={classes(['pdp-gallery-grid', className])}
      style={{
        '--gap': gap ? `var(--spacing-${gap})` : '0px',
        ...style,
      }}
    >
      {Children.map(
        children,
        (child: VNode<HTMLAttributes<HTMLElement>>, key) => (
          <div key={count + key} className="pdp-gallery-grid__item">
            <VComponent
              key={child.props.src}
              node={child}
              loading={key === 0 ? 'eager' : 'lazy'}
            />
          </div>
        )
      )}
    </div>
  );
};
